import {FC, useEffect, useRef} from 'react';
import { useDrag } from 'react-dnd';
import { twMerge } from 'tailwind-merge';
import { DropZoneItem } from '../gantt/GanttDropZone';
import { useGanttService } from '../gantt/GanttProvider';
import { DailyEditingType } from '@/types/daily.types';
import { IconType } from 'react-icons';

interface Props {
  color: string;
  textColor?: string;
  name: string;
  Icon?: IconType;
  item: DropZoneItem;
  readOnly?: boolean;
  className?: string;
  canFavourite?: boolean;
  taskId?: string;
  type?: DailyEditingType;
}

export const DragDropButton: FC<Props> = ({
  color,
  textColor = '#000000',
  name,
  item,
  Icon,
  readOnly,
  className,
  canFavourite
}) => {
  const innerRef = useRef<HTMLDivElement>(null);

  const [{ isDragging }, dragRef] = useDrag(
    () => ({
      type: item.type,
      item: item,
      collect: monitor => ({
        isDragging: monitor.isDragging()
      })
    }),
    [item]
  );

  // Attach dragRef to innerRef only if not readOnly
  useEffect(() => {
    if (!readOnly && innerRef.current) {
      dragRef(innerRef);
    }
  }, [readOnly, dragRef]);

  const { setCurrentlyDragging } = useGanttService(state => ({ setCurrentlyDragging: state.setCurrentlyDragging }));
  useEffect(() => {
    if (!isDragging) {
      setCurrentlyDragging(undefined);
    } else {
      setCurrentlyDragging(item.context ?? item.type);
    }
  }, [isDragging, setCurrentlyDragging, item.type, item.context]);

  return (
    <div
      ref={innerRef}
      style={{ background: color, color: textColor }}
      className={twMerge(
        'px-5 py-2 rounded-lg text-black flex justify-between items-center border shadow text-lg font-semibold select-none cursor-grab',
        className
      )}
    >
      <div className={twMerge('flex items-center gap-3 flex-1 overflow-hidden', !canFavourite && 'justify-center')}>
        {Icon && <Icon className='h-6 w-6'/>}
        <p className="truncate">{name}</p>
      </div>
    </div>
  );
};
